import './style.css';

import { View } from '@zinc/ui';
import { bind } from 'bind-decorator';
import withDisplayName from 'bz-utilities/hoc/display-name';
import cx from 'classnames';
import _ from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { colours } from '../../../styles/colours';
import Text from '../../general/text';
import { ClinicalBaseControl } from '../clinical-base-control';
import ReadOnly from '../read-only';
import Label from './controls/label';

export interface IProps {
  appendMode?: 'space' | 'newline' | 'newlinedash' | 'comma';
  errored?: boolean;
  'full-lookup-path'?: string;
  hideLabel?: boolean;
  inline?: boolean;
  label?: React.ReactNode;
  lists?: any;
  lookups?: any;
  lookupValueMember?: string;
  maitland?: boolean;
  open?: boolean;
  placeholder?: React.ReactNode;
  readOnly?: boolean;
  restrictFutureDate?: boolean;
  rows?: number;
  src?: string | string[];
  style?: React.CSSProperties;
  tabIndex: number;
  value?: string;
  warning?: boolean;
}

@observer
class ClinicalText extends React.Component<IProps> {
  static displayName = 'Clinical Text';
  static readonlyLabelStyle: React.CSSProperties = { color: colours.lightBlue, fontSize: 16 };
  static textStyle: React.CSSProperties = {
    color: colours.blue600,
    fontSize: 14,
    lineHeight: '14px',
    height: 20
  };

  static defaultProps = {
    errored: false,
    tabIndex: 1,
    value: null,
    onChange: () => null,
    onKeyDown: () => {}
  };

  id = crypto.randomUUID();

  input = React.createRef();

  constructor(props: IProps) {
    super(props);
  }

  render() {
    if (this.props.readOnly) {
      return (
        <ReadOnly
          labelStyle={ClinicalText.readonlyLabelStyle}
          className={cx({ maitland: this.props.maitland })}
          inline={this.props.inline}
          label={this.props.label}
          value={this.props.value}
          style={this.props.style}
        />
      );
    }

    let label;
    if (!this.props.inline && !this.props.hideLabel) {
      label = (
        <View direction="row" gap={2} align="center">
          <Label input={this.input} value={this.props.label} />
        </View>
      );
    }

    return (
      <div style={{ width: '100%' }}>
        <View direction="row" gap={2} align="center">
          {label}
        </View>
        <Text
          {...this.props}
          ref={this.input}
          style={ClinicalText.textStyle}
          appendMode={this.props.appendMode}
          hideLabel={true}
          appendMulti={true}
          inline={this.props.inline}
          maitland={this.props.maitland}
          open={this.props.open}
          placeholder={this.props.placeholder}
          rows={this.props.rows || 1}
          tabIndex={this.props.tabIndex}
          spellCheck={true}
          src={this.src}
          value={this.props.value}
          onChange={this.onChange}
          onMouseDown={this.onMouseDown}
          onKeyDown={this.props.onKeyDown}
        />
      </div>
    );
  }

  @bind
  onMouseDown(e) {
    const { 'full-lookup-path': fullLookupPath } = this.props;

    if (fullLookupPath) {
      return;
    }
  }

  @bind
  onChange(value, e) {
    if (this.props.autosuggest) {
      return this.props.onChange(e);
    } else {
      return this.props.onChange.call(this, value);
    }
  }

  get list() {
    let lists = this.props.lists || { _idToNameMap: {} };
    let list = lists[this.props.src];
    if (list) return list;
    try {
      return lists[lists._idToNameMap[this.props.src]];
    } catch (ex) {
      // this was a sneaky retro fit, so put in a catch all
      // to make sure existing behaviour was adhered to...
    }
    return null;
  }

  get src() {
    let src = this.props.src || [];
    if (_.isString(src)) {
      const { list } = this;
      if (list != null) {
        return list;
      } else {
        return (
          __guard__(
            this.props.lookups[src] ||
              _.find(this.props.lookups, (x) => x[this.props.lookupValueMember || 'name'] === src),
            (x) => x.values
          ) || []
        );
      }
    } else if (_.isString(src[0])) {
      const temp = [];
      src.forEach((item) => (temp[item] = item));
      return temp;
    }

    return src;
  }
}

const ClinicalTextWrapper = withDisplayName((props: any) => {
  return <ClinicalText {...props} />;
}, 'ClinicalTextWrapper');

export default ClinicalBaseControl(ClinicalTextWrapper);

function __guard__(value, transform) {
  return typeof value !== 'undefined' && value !== null ? transform(value) : undefined;
}
